import { memo, useRef, useMemo } from 'react';
import { matchPath, useLocation } from 'react-router-dom';

import { useComponentCategories } from '../../utils/useComponentCategories';
import {
  getCategoryBackgroundImage,
  getCategoryPromoUrl,
  getCategoryVerticalImage,
} from '../../../shared/helpers/categoryHelpers';
import { Splash } from '../Splash/Splash';
import { useVendor } from '../../VendorPovider/VendorPovider';
import { useEpisodeData } from '../EpisodeButton/useEpisodeData';
import { getPath, routeNames } from '../../../shared/helpers/routes';
import { CategoryButtons } from '../SplashButtons/CategoryButtons';
import { useViewportType } from '../../utils/useViewportType';
import { useSplashTracking } from '../../PageComponents/common/useSplashTracking';
import { useViewObserver } from '../../utils/useViewObserver';
import { getImageSources } from '../../../helpers/images';
import { selectCategoryById } from '../../../modules/categories';
import { useAppSelector } from '../../../modules/hooks';

import { getCategoryLabels } from './getCategoryLabels';

import type { CategorySplashComponent } from '@vgtv/api-client/lib/component/splash';
import type { FC } from 'react';

interface CategorySplashProps {
  componentConfig: CategorySplashComponent;
  isFirstComponent: boolean;
  pageComponentIndex: number;
}

const CategorySplashBase: FC<CategorySplashProps> = (
  props: CategorySplashProps
) => {
  const { componentConfig, isFirstComponent, pageComponentIndex } = props;
  const { id, overrides = {} } = componentConfig;
  const [category] = useComponentCategories(id);

  const ref = useRef<HTMLDivElement>(null!);

  const { trackSplashView, trackButtonSplashClick } = useSplashTracking({
    elementType: 'CategorySplash',
    pageComponentIndex,
  });

  useViewObserver({
    onView: trackSplashView,
    elementRef: ref,
  });

  const previewUrl = getCategoryPromoUrl(category);
  const image = overrides.image ?? getCategoryBackgroundImage(category);
  const title = category.title;
  const description =
    overrides.description ?? category.additional?.description ?? '';
  const { vendor } = useVendor();
  const { label } = useEpisodeData({ category, vendor });
  const categoryUrl = getPath(routeNames.category, {
    vendor,
    category,
  });
  const { isMobile } = useViewportType();
  const location = useLocation();
  const verticalImage = getCategoryVerticalImage(category);
  const isPulledUp = isFirstComponent && image !== undefined && !isMobile;

  const parentCategory = useAppSelector((state) =>
    selectCategoryById(state, vendor, category.parentId)
  );

  const verticalImageSources = useMemo(
    () =>
      getImageSources({
        imageUrl: verticalImage,
        aspectRatio: 'vertical',
        imagePreset: 'assetsSliderVertical',
      }),
    [verticalImage]
  );

  return (
    <Splash
      ref={ref}
      isPulledUp={isPulledUp}
      imageUrl={image}
      redirectImageUrl={
        !matchPath(location.pathname, categoryUrl) && isMobile
          ? categoryUrl
          : undefined
      }
      previewUrl={previewUrl}
      title={title}
      description={description}
      lazy={!isFirstComponent}
      subtitle={isMobile ? label : ''}
      buttons={
        <CategoryButtons
          trackSplashClick={trackButtonSplashClick}
          category={category}
        ></CategoryButtons>
      }
      verticalImageSources={verticalImage ? verticalImageSources : undefined}
      labels={getCategoryLabels(category, parentCategory)}
    />
  );
};

export const CategorySplash = memo(CategorySplashBase);
